import { Link } from "@tanstack/react-router";
import {
  LucideIcon,
  RotateCcw,
  SearchX,
  ShieldOff,
  TriangleAlert,
} from "lucide-react";

import { Button, buttonVariants } from "@components/ui/button";

import {
  CASE_ACCESS_ACTIONS,
  CASE_ACCESS_COPY,
  CASE_ACCESS_STATE,
  type CaseAccessProblem,
} from "@constants/case-access";
import { NAVIGATION_PATHS } from "@constants/navigation";

import { cn } from "@utils/ui";

type CaseAccessNoticeProps = {
  state: CaseAccessProblem;
  /** Only wired up for the retryable state; ignored otherwise. */
  onRetry?: () => void;
  isRetrying?: boolean;
};

/**
 * Per-state icon and tint. Each maps to an existing semantic colour ramp
 * (tailwind.config.js) so the notice tracks the theme in both light and dark
 * rather than hard-coding hexes:
 *
 * - forbidden → warning. A closed door, not a fault; nothing is broken and
 *   nothing the viewer does will change it.
 * - unavailable → neutral foreground. Deliberately undramatic — the case may
 *   simply not be theirs yet, and an alarming colour would imply otherwise.
 * - error → destructive. Something actually failed.
 */
const PRESENTATION: Record<
  CaseAccessProblem,
  { Icon: LucideIcon; ring: string; icon: string }
> = {
  [CASE_ACCESS_STATE.FORBIDDEN]: {
    Icon: ShieldOff,
    ring: "bg-warning-50 ring-warning-100",
    icon: "text-warning-600",
  },
  [CASE_ACCESS_STATE.UNAVAILABLE]: {
    Icon: SearchX,
    ring: "bg-background-muted ring-border",
    icon: "text-foreground-muted",
  },
  [CASE_ACCESS_STATE.ERROR]: {
    Icon: TriangleAlert,
    ring: "bg-destructive-50 ring-destructive-100",
    icon: "text-destructive-600",
  },
};

/**
 * What the case shell renders instead of the tab bar and the child route when
 * the case can't be read (MDRS-343).
 *
 * Replaces the whole subtree rather than sitting above it: every tab's own
 * queries hit the same case and would fail the same way, and the header's action
 * buttons would offer operations that can only 403. Note this is a teardown, not
 * a prevention — the tabs mount on first render while the case fetch is still in
 * flight, so their requests do go out and are refused server-side.
 *
 * Announced via `role="status"` because it arrives after the shell has already
 * painted. Focus is deliberately left alone: moving it mid-navigation is more
 * disorienting than the announcement is worth (frontend-accessibility skill).
 * Caveat accepted knowingly — a live region inserted together with its content
 * announces less reliably than one already in the DOM, so a screen-reader user
 * may have to navigate to it.
 */
export const CaseAccessNotice = ({
  state,
  onRetry,
  isRetrying = false,
}: CaseAccessNoticeProps) => {
  const copy = CASE_ACCESS_COPY[state];
  const { Icon, ring, icon } = PRESENTATION[state];
  const canRetry = copy.canRetry && Boolean(onRetry);

  return (
    <section
      role="status"
      aria-live="polite"
      className="flex h-full items-center justify-center rounded-xl border border-border bg-background-white p-6"
    >
      {/* max-w caps the measure at a readable line length; the copy is centred
          because there's no surrounding content to align to. */}
      <div className="flex max-w-[52ch] flex-col items-center gap-5 text-center">
        <span
          className={cn(
            "flex size-14 items-center justify-center rounded-full ring-1",
            ring
          )}
        >
          <Icon className={cn("size-6", icon)} aria-hidden="true" />
        </span>

        <div className="space-y-2">
          <h2 className="text-balance text-lg font-semibold text-foreground">
            {copy.title}
          </h2>
          <p className="text-pretty text-sm leading-relaxed text-foreground-muted">
            {copy.body}
          </p>
        </div>

        {/* Secondary action left, primary right — the order and the
            outline/default pairing used by ui/ConfirmationModal, so the notice
            reads like the rest of the app's decision points. Where a retry is
            possible it IS the primary action: leaving the emphasis on "leave the
            page" would push people away from the one thing that might work. */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          {/* A real anchor, not a button with a navigate handler: it should
              middle-click, right-click and read as a link. Styled with
              `buttonVariants` rather than `<Button asChild>` — Button always
              renders its loading slot beside `children`, so Radix's Slot gets an
              array and throws. Same approach as ui/pagination.tsx. */}
          <Link
            to={NAVIGATION_PATHS.REPORT_STUDIO}
            className={cn(
              buttonVariants({ variant: canRetry ? "simple-outline" : "default" })
            )}
          >
            {CASE_ACCESS_ACTIONS.back}
          </Link>
          {canRetry && (
            <Button onClick={onRetry} isLoading={isRetrying}>
              {!isRetrying && <RotateCcw aria-hidden="true" />}
              {CASE_ACCESS_ACTIONS.retry}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
